import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_SEO = {
  title: "SynergyCoin | Crypto Investment and Referral Platform",
  description:
    "SynergyCoin is a crypto investment and referral platform with guided onboarding, package access, and community-driven growth.",
  robots: "index, follow",
};

const ROUTE_SEO = [
  {
    match: (pathname) => pathname === "/",
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname === "/packages",
    title: "SynergyCoin Packages | Investment Plans and Access Levels",
    description:
      "Review SynergyCoin package options, compare access levels, and choose the plan that fits your investment goals.",
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname === "/counter",
    title: "SynergyCoin Investment Counter | Estimate Package Value",
    description:
      "Estimate SynergyCoin package values and review investment figures before continuing to signup or checkout.",
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname === "/privacy-policy",
    title: "SynergyCoin Privacy Policy | Data Use, Accounts, and Transactions",
    description:
      "Read the SynergyCoin privacy policy, including how account, transaction, and usage data are handled.",
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname === "/terms-of-service",
    title: "SynergyCoin Terms of Service | Platform Rules and User Responsibilities",
    description:
      "Review the SynergyCoin terms of service, platform rules, risk notices, and account responsibilities.",
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname === "/cookie-settings",
    title: "SynergyCoin Cookie Settings | Privacy and Tracking Preferences",
    description:
      "Manage SynergyCoin cookie preferences for essential, analytics, marketing, and experience settings.",
    robots: "index, follow",
  },
  {
    match: (pathname) => pathname.startsWith("/join/"),
    title: "Referral Invitation | SynergyCoin",
    description:
      "Join SynergyCoin through a referral invitation and continue the signup flow with your referral code applied.",
    robots: "noindex, nofollow",
  },
  {
    match: (pathname) =>
      [
        "/login",
        "/signup",
        "/forgot-password",
        "/forgot-password/verify",
        "/forgot-password/reset",
        "/referrer-entry",
        "/checkout",
        "/payment-status",
        "/dashboard",
        "/dashboard/packages",
        "/referrer-link",
        "/transactions",
        "/community",
        "/roadmap",
        "/rewards",
        "/notifications",
        "/profile",
      ].includes(pathname),
    title: "Account | SynergyCoin",
    description:
      "Secure SynergyCoin account area for authentication, payments, referrals, dashboards, and member activity.",
    robots: "noindex, nofollow",
  },
];

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  return element;
};

const ensureLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  return element;
};

const getSeoForPath = (pathname) =>
  ROUTE_SEO.find((entry) => entry.match(pathname)) || DEFAULT_SEO;

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const { pathname } = location;
    const seo = getSeoForPath(pathname);
    const canonicalOrigin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://synergycoinx.com";
    const canonicalUrl = `${canonicalOrigin}${pathname === "/" ? "/" : pathname}`;

    document.title = seo.title;

    ensureMeta('meta[name="description"]', {
      name: "description",
      content: seo.description,
    });
    ensureMeta('meta[name="robots"]', {
      name: "robots",
      content: seo.robots,
    });
    ensureMeta('meta[http-equiv="X-Robots-Tag"]', {
      "http-equiv": "X-Robots-Tag",
      content: seo.robots,
    });
    ensureMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    ensureMeta('meta[property="og:title"]', {
      property: "og:title",
      content: seo.title,
    });
    ensureMeta('meta[property="og:description"]', {
      property: "og:description",
      content: seo.description,
    });
    ensureMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    ensureMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    ensureMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: seo.title,
    });
    ensureMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.description,
    });
    ensureLink('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });
  }, [location]);

  return null;
}
